import { IBrand, IStaffer } from '@principle-theorem/principle-core/interfaces';
import { IReffable, WithRef } from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Staffer } from '../staffer/staffer';

/**
 * AI Scribe seat management — the billing hook point.
 *
 * A practitioner "seat" is the $99/practitioner billing source of truth, held
 * at brand level and flagged per-staffer at `metadata.aiScribe.seatEnabled`.
 * (`IScribeSession.durationSeconds` is analytics-only and is never billed.)
 *
 * The separate billing workstream consumes this class as its query surface:
 * `enabledSeats$` / `enabledSeatCount$` report who is billable, and the
 * `metadata.aiScribe.seatEnabled` flag is what they charge on. Keep this
 * interface clean, typed, and additive.
 */
export class AiScribeSeats {
  /**
   * The brand's practitioners who currently hold an AI Scribe seat. The brand's
   * staff are filtered in memory on `metadata.aiScribe.seatEnabled === true` —
   * intentionally without a Firestore composite index, since the per-brand
   * staff count is small.
   */
  static enabledSeats$(
    brand: IReffable<IBrand>
  ): Observable<WithRef<IStaffer>[]> {
    return Staffer.practitionersByBrand$(brand).pipe(
      map((staff) =>
        staff.filter((staffer) => AiScribeSeats.isSeatEnabled(staffer))
      )
    );
  }

  /**
   * The number of billable AI Scribe seats for the brand — the count the
   * billing workstream charges $99 against.
   */
  static enabledSeatCount$(brand: IReffable<IBrand>): Observable<number> {
    return AiScribeSeats.enabledSeats$(brand).pipe(
      map((seats) => seats.length)
    );
  }

  /** Whether a staffer currently holds an AI Scribe seat. */
  static isSeatEnabled(staffer: IStaffer | undefined): boolean {
    return staffer?.metadata?.aiScribe?.seatEnabled === true;
  }

  /**
   * Whether the current user may use AI Scribe. A practitioner qualifies by
   * holding a billed seat; a management user always qualifies, so they can
   * test the feature without being assigned a seat.
   */
  static hasScribeAccess(
    staffer: IStaffer | undefined,
    isManagement: boolean
  ): boolean {
    return isManagement || AiScribeSeats.isSeatEnabled(staffer);
  }

  /**
   * The gate for surfacing the AI Scribe record button: the organisation has
   * opted into `ai-scribe` and the user may use it (seat holder or
   * management). Pass the signed-in staffer, not the clinician chosen under
   * Recording As: the seat follows whoever is signed in.
   */
  static isAiScribeActive(
    aiScribeEnabled: boolean,
    staffer: IStaffer | undefined,
    isManagement: boolean
  ): boolean {
    return (
      aiScribeEnabled && AiScribeSeats.hasScribeAccess(staffer, isManagement)
    );
  }
}
